import { useCallback, useRef, useState } from 'react';
import {
    IMAGE_NODE_WIDTH,
    IMAGE_NODE_HEIGHT,
    IMAGE_NODE_PREFIX,
    IMAGE_NODE_GAP,
} from '../constants/canvas';
import { EXAMPLE_CARD_SAMPLE_IMAGE_BASE_URL } from '@view/page_v3/CreativeToImage/components/ExampleCards';
import { uploadFlowImage } from '../execution';
import { createNodeId } from '../utils/common';
import { buildCreativeBootstrapNodePatch } from '../utils/common';

/**
 * 画布图片上传与节点创建管理
 */
export const useCanvasUpload = ({
    reactFlowRef,
    canvasRef,
    nodesRef,
    edgesRef,
    setNodes,
    setNextImageNodeNumber,
    nextImageNodeNumberRef,
    ensureCanvasProject,
    pushHistory,
    persistCanvasBatch,
    handleAddNode,
    isPmView,
}) => {
    const uploadInputRef = useRef(null);
    const canvasExampleCreatingRef = useRef(false);
    const [canvasUploadCount, setCanvasUploadCount] = useState(0);

    // 批量创建图片节点
    const createImageNodes = useCallback(async (sources, options = {}) => {
        const reactFlow = reactFlowRef.current;
        const canvas = canvasRef.current;
        if (!reactFlow || !canvas || sources.length === 0) return;
        const projectContext = await ensureCanvasProject();
        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        pushHistory();

        const bounds = canvas.getBoundingClientRect();
        const centerPosition = options.flowPosition || reactFlow.screenToFlowPosition({
            x: bounds.left + bounds.width / 2,
            y: bounds.top + bounds.height / 2,
        });
        const totalWidth = (sources.length * IMAGE_NODE_WIDTH) + ((sources.length - 1) * IMAGE_NODE_GAP);
        const startX = centerPosition.x - (totalWidth / 2);
        const baseY = centerPosition.y - (IMAGE_NODE_HEIGHT / 2);

        const appendedNodes = sources.map((src, index) => ({
            id: createNodeId('image-node'),
            type: 'imageNode',
            position: {
                x: startX + (index * (IMAGE_NODE_WIDTH + IMAGE_NODE_GAP)),
                y: baseY,
            },
            data: {
                label: `${IMAGE_NODE_PREFIX}${nextImageNodeNumberRef.current + index}`,
                imageSequence: nextImageNodeNumberRef.current + index,
                imageSequenceLabel: `${IMAGE_NODE_PREFIX}${nextImageNodeNumberRef.current + index}`,
                src,
                inputParams: {
                    output: src,
                },
            },
        }));
        const nextNodes = beforeNodes.concat(appendedNodes);

        setNodes(nextNodes);
        setNextImageNodeNumber((prev) => prev + sources.length);
        await persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: beforeEdges,
            projectId: projectContext.projectId,
        });
    }, [ensureCanvasProject, persistCanvasBatch, pushHistory, setNodes, reactFlowRef, canvasRef, nodesRef, edgesRef, nextImageNodeNumberRef, setNextImageNodeNumber]);

    // 将单张历史图片回填到画布
    const handleSendHistoryImage = useCallback((url) => {
        if (!url) return;
        createImageNodes([url]);
    }, [createImageNodes]);

    const handleSelectCanvasExample = useCallback(async (item) => {
        if (canvasExampleCreatingRef.current) return;

        const imageUrls = Array.isArray(item?.sampleImage)
            ? item.sampleImage
                .filter(Boolean)
                .map((name) => `${EXAMPLE_CARD_SAMPLE_IMAGE_BASE_URL}${name}`)
            : [];

        try {
            canvasExampleCreatingRef.current = true;
            await handleAddNode('creativeImg2Img', {
                autoOpenFunctionPanel: true,
                dataPatch: buildCreativeBootstrapNodePatch({
                    imageUrls,
                    prompt: item?.text || '',
                    aspectRatio: '3:4',
                    imageSize: '2K',
                    num: 4,
                    shouldAutoGenerate: false,
                }),
            });
        } catch (error) {
            console.error(error);
        } finally {
            window.setTimeout(() => {
                canvasExampleCreatingRef.current = false;
            }, 0);
        }
    }, [handleAddNode]);

    // 将多张历史图片批量回填到画布
    const handleBatchSendHistoryImages = useCallback((urls) => {
        const validUrls = (urls || []).filter(Boolean);
        if (!validUrls.length) return;
        return createImageNodes(validUrls);
    }, [createImageNodes]);

    const handleSelectMaterialLibraryImage = useCallback((value) => {
        const urls = Array.isArray(value) ? value.filter(Boolean) : [value].filter(Boolean);
        if (!urls.length) return;
        createImageNodes(urls);
    }, [createImageNodes]);

    // 上传本地图片后，生成对应的画布节点
    const uploadAndCreateImageNodes = useCallback(async (files, options = {}) => {
        if (!files.length) return;

        const imageFiles = files.filter((file) => file?.type?.startsWith('image/'));
        if (imageFiles.length === 0) return;

        setCanvasUploadCount((prev) => prev + 1);
        try {
            const sources = await Promise.all(imageFiles.map(async (file) => {
                try {
                    return await uploadFlowImage(file);
                } catch (error) {
                    return '';
                }
            }));
            const uploadedSources = sources.filter(Boolean);
            if (uploadedSources.length > 0) {
                await createImageNodes(uploadedSources, options);
            }
        } finally {
            setCanvasUploadCount((prev) => Math.max(0, prev - 1));
        }
    }, [createImageNodes]);

    // 处理本地文件选择上传
    const handleUploadFiles = useCallback(async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (files.length === 0) return;

        await uploadAndCreateImageNodes(files);
    }, [uploadAndCreateImageNodes]);

    // 处理文件拖入画布时的交互反馈
    const handleCanvasDragOver = useCallback((event) => {
        if (isPmView) return;
        const hasFile = Array.from(event.dataTransfer?.types || []).includes('Files');
        if (!hasFile) return;

        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
    }, [isPmView]);

    // 处理文件拖拽到画布后的上传与落点创建
    const handleCanvasDrop = useCallback(async (event) => {
        if (isPmView) return;
        const reactFlow = reactFlowRef.current;
        const canvas = canvasRef.current;
        if (!reactFlow || !canvas) return;

        const files = Array.from(event.dataTransfer?.files || []);
        const imageFiles = files.filter((file) => file?.type?.startsWith('image/'));
        if (imageFiles.length === 0) return;

        event.preventDefault();
        event.stopPropagation();

        const flowPosition = reactFlow.screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
        });

        await uploadAndCreateImageNodes(imageFiles, { flowPosition });
    }, [isPmView, uploadAndCreateImageNodes, reactFlowRef, canvasRef]);

    return {
        uploadInputRef,
        canvasUploadCount,
        uploadAndCreateImageNodes,
        handleUploadFiles,
        handleCanvasDragOver,
        handleCanvasDrop,
        createImageNodes,
        handleSendHistoryImage,
        handleSelectCanvasExample,
        handleBatchSendHistoryImages,
        handleSelectMaterialLibraryImage,
    };
};